import { Component, OnDestroy, OnInit } from '@angular/core';
import { GeneralActions, NavBreadcrumb } from '@models/models';
import { SettingsProvider } from '@services/settings/settings';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MessageService } from '@services/helpers/message-service/message.service';
import { CalendarIntegrationProvider } from '@services/calendar-integration/calendar-integration.service';
import { Location } from '@angular/common';
import { SNModalService } from '@web-services/modal/snModal.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmReSyncModal } from '@web-components/settings/date-time-calendar/confirm-re-sync-modal/confirm-re-sync-modal';
import { DateTimeSettings, TimeZone } from '@models/settings.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
	selector: 'date-time-calendar',
	template: require('./date-time-calendar.html'),
	styles: [require('./date-time-calendar.scss')]
})
export class DateTimeCalendarComponent implements OnInit, OnDestroy {
	activationGoogleCode: string | null = null;
	lockIntegration = false;
	activatedCalendar = false;
	isGoogleSynchronizationActive = false;
	isGoogleIntegrationActive = false;
	isGoogleDisconnectionActive = false;
	isCalendarSettingsSaveActive = false;
	unsubscribe: Subject<void> = new Subject<void>();
	calendarSettings: DateTimeSettings;
	reSyncModal: NgbModalRef;
	dateTimeForm: FormGroup;
	timeZones: TimeZone[];
	actions: GeneralActions;

	breadcrumbs: Array<NavBreadcrumb> = [
		{ link: '/', title: 'Dashboard' },
		{ link: '/settings', title: 'Settings' },
		{ link: null, title: 'Date, Time & Calendar' }
	];

	constructor(
		private settingsService: SettingsProvider,
		private messageService: MessageService,
		private calendarIntegrationService: CalendarIntegrationProvider,
		private location: Location,
		private modalService: SNModalService
	) {
		this.dateTimeForm = new FormGroup({
			weekStartsOn: new FormControl('', Validators.required),
			dateFormat: new FormControl('', Validators.required),
			timeFormat: new FormControl('', Validators.required),
			timeZone: new FormControl('', Validators.required)
		});
	}

	ngOnInit(): void {
		this.subscribeToActivationCode();
		this.subscribeToIntegrationChanges();
		this.subscribeToIntegrationStatus();
		this.integrateGoogleCalendar();
		this.initCalendarSettings();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	calendarIntegrationStart(): void {
		this.calendarIntegrationService.setLookGoogleIntegration('integrate');
		this.calendarIntegrationService.connectGoogleCalendar().subscribe(
			({ message }): void => {
				window.location.assign(message);
			},
			(): void => {
				this.handleCalendarIntegrationError();
			}
		);
	}

	disconnectGoogle(): void {
		this.calendarIntegrationService.setLookGoogleIntegration('disconnect');
		this.calendarIntegrationService.disconnectGoogleCalendar().subscribe(
			(): void => {
				this.messageService.showToaster('s_2', 'googleCalendar');
			},
			(): void => {
				this.handleCalendarIntegrationError();
			},
			(): void => {
				this.calendarIntegrationService.stopCalendarInterval();
				this.handleCalendarIntegrationError();
			}
		);
	}

	showSynchronize(): void {
		this.reSyncModal = this.modalService.openModal(ConfirmReSyncModal);
		this.modalService
			.getModalData()
			.filter((data): boolean => !!data)
			.take(1)
			.subscribe((): void => this.synchronize());
	}

	saveCalendarSettings(): void {
		this.isCalendarSettingsSaveActive = true;
		this.settingsService
			.updateDateTimeSettings(
				this.actions.SAVE_DATE_TIME_SETTINGS.url,
				this.dateTimeForm.value
			)
			.subscribe(
				(): void => {
					this.settingsService
						.getSettingsRequest()
						.pipe(takeUntil(this.unsubscribe))
						.subscribe();
					this.messageService.showToaster('s_0', 'calendar');
				},
				(error): void => {
					this.messageService.showToaster('e_0', 'general', error.error);
				},
				(): void => {
					this.isCalendarSettingsSaveActive = false;
				}
			);
	}

	private synchronize(): void {
		this.calendarIntegrationService.setLookGoogleIntegration('synchronize');
		this.calendarIntegrationService.synchronizeGoogleCalendar().subscribe(
			(): void => {
				this.checkResultCalendarSynchronize();
			},
			(): void => {
				this.handleCalendarIntegrationError();
			}
		);
	}

	private initCalendarSettings(): void {
		this.settingsService.getDateTimeSettingsV1().subscribe(({ data }): void => {
			this.createDateTimeForm(data);
		});
	}

	private createDateTimeForm(data): void {
		this.timeZones = data.availableTimeZones;
		this.actions = data.actions;

		this.dateTimeForm.patchValue({ ...data, timeZone: data.timeZone.name });
	}

	private integrateGoogleCalendar(): void {
		const url = new URL(window.location.href);
		const googleCode = url.searchParams.get('code');
		if (googleCode !== null) {
			if (this.activationGoogleCode !== googleCode) {
				this.calendarIntegrationService.setGoogleActivationCode(googleCode);
				this.calendarIntegrationService.setLookGoogleIntegration('integrate');
				this.messageService.showToaster('i_0', 'googleCalendar');
				this.save(googleCode);
			}
		} else {
			this.calendarIntegrationService.setGoogleActivationCode(null);
			this.getActivatedCalendar();
		}
	}

	private subscribeToActivationCode(): void {
		this.calendarIntegrationService.getGoogleActivationCode
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((code): string => (this.activationGoogleCode = code));
	}

	private subscribeToIntegrationChanges(): void {
		this.calendarIntegrationService.getLookGoogleIntegration
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data): void => {
				this.lockIntegration = data !== 'disable';
				this.isGoogleIntegrationActive = data === 'integrate';
				this.isGoogleSynchronizationActive = data === 'synchronize';
				this.isGoogleDisconnectionActive = data === 'disconnect';
			});
	}

	private subscribeToIntegrationStatus(): void {
		this.calendarIntegrationService.getIsIntegrated
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data): boolean => (this.activatedCalendar = data));
	}

	private getActivatedCalendar(): void {
		this.calendarIntegrationService.getActivatedCalendar().subscribe(
			({ data }): void => {
				if (data.state === 'INTEGRATED') {
					this.calendarIntegrationService.setIsIntegrated(true);
					if (!this.isGoogleDisconnectionActive) {
						this.calendarIntegrationService.setLookGoogleIntegration('disable');
					}
					this.calendarIntegrationService.stopCalendarInterval();
				} else if (data.state === 'SYNCHRONIZATION') {
					this.calendarIntegrationService.setIsIntegrated(true);
					if (!this.isGoogleSynchronizationActive) {
						this.checkResultCalendarIntegration();
					}
					this.calendarIntegrationService.setLookGoogleIntegration(
						'synchronize'
					);
				} else {
					this.calendarIntegrationService.setIsIntegrated(false);
					this.calendarIntegrationService.setLookGoogleIntegration('disable');
				}
			},
			(): void => {
				this.handleCalendarIntegrationError();
			}
		);
	}

	private handleCalendarIntegrationError(): void {
		this.calendarIntegrationService.setIsIntegrated(false);
		this.calendarIntegrationService.setLookGoogleIntegration('disable');
		this.removeParamsFromUrl();
	}

	private removeParamsFromUrl(): void {
		this.location.go(window.location.pathname);
	}

	private save(code): void {
		this.calendarIntegrationService.updateGoogleCalendarStatus(code).subscribe(
			(): void => {
				this.checkResultCalendarIntegration();
			},
			(): void => {
				this.handleCalendarIntegrationError();
			}
		);
	}

	private checkResultCalendarIntegration(): void {
		this.calendarIntegrationService.setLookGoogleIntegration('integrate');
		this.calendarIntegrationService.startCalendarinterval();
		this.calendarIntegrationService.calendarInterval().subscribe(
			({ data }): void => {
				if (data.state === 'INTEGRATED') {
					this.calendarIntegrationService.stopCalendarInterval();
				}
			},
			(): void => {
				this.handleCalendarIntegrationError();
			},
			(): void => {
				this.completeCalendarIntegration();
			}
		);
	}

	private completeCalendarIntegration(): void {
		this.calendarIntegrationService.setLookGoogleIntegration('disable');
		this.calendarIntegrationService.setIsIntegrated(true);
		this.messageService.showToaster('s_1', 'googleCalendar');
		this.removeParamsFromUrl();
	}

	private checkResultCalendarSynchronize(): void {
		this.calendarIntegrationService.startCalendarinterval();
		this.calendarIntegrationService.calendarInterval().subscribe(
			({ data }): void => {
				if (data.state === 'INTEGRATED') {
					this.calendarIntegrationService.stopCalendarInterval();
				}
			},
			(): void => {
				this.handleCalendarIntegrationError();
			},
			(): void => {
				this.reSyncModal.close();
				this.calendarIntegrationService.setLookGoogleIntegration('disable');
				this.messageService.showToaster('s_1', 'googleCalendar');
				this.removeParamsFromUrl();
			}
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/settings/date-time-calendar/date-time-calendar.ts